import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { Token } from '../../../interfaces/token';
import { AuthService } from '../../services/auth/auth.service';
import {
  mapRequestWithHeaders,
  PUBLIC_ENDPOINTS,
  TRANSLATE_API_URL,
} from '../auth/auth.interceptor';

const TOKEN_REFRESH_BUFFER_MS = 30_000;

export const refreshTokenInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const token = authService.token();

  // Skip refresh token logic for translation API (public endpoint)
  if (
    request.url.includes(TRANSLATE_API_URL) ||
    PUBLIC_ENDPOINTS.some((url) => request.url.includes(url)) ||
    !token
  ) {
    return next(request);
  }

  if (shouldRefreshBeforeRequest(token)) {
    return refreshAndRetry(authService, router, request, next);
  }

  return next(request).pipe(
    catchError((err) => {
      if (
        err instanceof HttpErrorResponse &&
        typeof err.error?.message === 'string' &&
        ['user not found', 'invalid token'].includes(err.error?.message?.toLowerCase())
      ) {
        authService.clearTokenState();
        router.navigate(['/']);
        return throwError(() => err);
      }

      if (
        err instanceof HttpErrorResponse &&
        (err.status === 400 || err.status === 401 || err.status === 463) &&
        typeof err.error?.message === 'string'
      ) {
        const m =
          err.error.originalMessage?.toLowerCase() ?? err.error.message?.toLowerCase() ?? '';
        const n = (err.error?.name ?? '').toString().toLowerCase();
        if (
          (m.includes('expir') &&
            (m.includes('jwt') || m.includes('token') || m.includes('session'))) ||
          m.includes('jwt expired') ||
          m.includes('invalid signature') ||
          m.includes('invalid jwt token') ||
          n.includes('jwt')
        ) {
          return refreshAndRetry(authService, router, request, next, err);
        }
      }

      return throwError(() => err);
    })
  );
};

function shouldRefreshBeforeRequest(token: Token): boolean {
  const expiresAtMs = getAccessTokenExpiresAtMs(token);

  if (expiresAtMs == null) {
    return false;
  }

  return expiresAtMs - Date.now() <= TOKEN_REFRESH_BUFFER_MS;
}

function getAccessTokenExpiresAtMs(token: Token): number | null {
  const expiresAtMs = new Date(token.expiresAt).getTime();

  if (Number.isNaN(expiresAtMs)) {
    return null;
  }

  return expiresAtMs;
}

function refreshAndRetry(
  authService: AuthService,
  router: Router,
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  originalError?: unknown
): Observable<HttpEvent<unknown>> {
  return authService.fetchRefreshToken().pipe(
    switchMap((result) => {
      if (result) {
        return next(mapRequestWithHeaders(authService.token(), request));
      }

      authService.clearTokenState();
      router.navigate(['/']);
      return throwError(() => originalError ?? new Error('Unable to refresh token'));
    })
  );
}
